import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from 'react-router-dom';
import {
    Button,
    Flex,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Grid,
    Input,
    Select,
    useColorModeValue,
} from "@chakra-ui/react";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import { personsService } from "services/personsService";
import { ROUTES } from "../../../constants/routes-path";
import {
    fetchAddressByCEP,
    formatCEP,
    formatDocument,
    formatPhoneNumber,
    validateCpfOrCnpj,
    validateEmail,
    validatePhone,
    ufs
} from "../../../utils/utils";
import ListPageCardHeader from 'components/Card/ListPageCardHeader';
import { FileAttachmentInput } from "../../../components/FileAttachmentInput/FileAttachmentInput";
import WebcamCapture from "../../../components/WebcamCapture/WebcamCapture";
import Loading from 'components/Loading/Loading';
import { checkErrors, hasError } from 'utils/forms-utils';

const PersonEditor = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { id } = useParams();
    const showToast = useCustomToast();
    const textColor = useColorModeValue("gray.700", "white");
    const [formData, setFormData] = useState();
    const [inputError, setInputError] = useState({});
    const fileInputRef = useRef(undefined);

    const initFormData = (values) => {
        setFormData({
            document: {
                validations: [{ required: true }],
                value: values?.cpf_cnpj ? formatDocument(values.cpf_cnpj) : ""
            },
            name: {
                validations: [{ required: true }],
                value: values?.nome || ""
            },
            socialName: {
                validations: [],
                value: values?.nome_social || ""
            },
            surname: {
                validations: [],
                value: values?.apelido || ""
            },
            cep: {
                validations: [],
                value: values?.endereco_cep || ""
            },
            address: {
                validations: [],
                value: {
                    logradouro: values?.endereco_logradouro || "",
                    numero: values?.endereco_numero || "",
                    bairro: values?.endereco_bairro || "",
                    localidade: values?.endereco_municipio || "",
                    uf: values?.endereco_uf || ""
                }
            },
            phone: {
                validations: [],
                value: values?.telefone || ""
            },
            cellphone: {
                validations: [],
                value: values?.celular || ""
            },
            email: {
                validations: [],
                value: values?.email || ""
            },
            issuingAgency: {
                validations: [],
                value: values?.orgao_expedidor_identidade || ""
            },
            rg: {
                validations: [],
                value: values?.numero_identidade || ""
            },
            ufRg: {
                validations: [],
                value: values?.id_uf_identidade || ""
            },
            photo: {
                validations: [],
                value: values?.foto || null
            },
            webcamImage: {
                validations: [],
                value: values?.webcam_image || null
            }
        });
    };

    const handleCEPChange = async (e) => {
        const formattedCEP = formatCEP(e.target.value);

        setFormData(prev => ({
            ...prev,
            cep: { ...prev.cep, value: formattedCEP }
        }));

        if (formattedCEP.length === 9) {
            try {
                const fetchedAddress = await fetchAddressByCEP(formattedCEP.replace("-", ""));
                if (fetchedAddress) {
                    setFormData(prev => ({
                        ...prev,
                        address: {
                            ...prev.address,
                            value: {
                                logradouro: fetchedAddress.logradouro || "",
                                numero: "",
                                bairro: fetchedAddress.bairro || "",
                                localidade: fetchedAddress.localidade || "",
                                uf: fetchedAddress.uf || ""
                            }
                        }
                    }));
                }
            } catch {
                showToast({ title: t("system.messages.errors.invalid-cep"), status: "error" });
            }
        }
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            address: {
                ...prev.address,
                value: {
                    ...prev.address.value,
                    [name]: value,
                },
            },
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let formatted = value;

        if (name === "document") formatted = formatDocument(value);
        else if (name === "phone" || name === "cellphone") formatted = formatPhoneNumber(value);

        setFormData((prev) => ({
            ...prev,
            [name]: { ...prev[name], value: formatted },
        }));

        setInputError((prev) =>
            checkErrors({
                formData,
                inputError: prev,
                values: { ...formData, [name]: { ...formData[name], value: formatted } },
                name,
            })
        );
    };

    const toFormData = () => {
        const personData = {
            cpf_cnpj: formData.document.value.replace(/\D/g, ""),
            nome: formData.name.value,
            nome_social: formData.socialName.value,
            apelido: formData.surname.value,
            endereco_cep: formData.cep.value,
            endereco_logradouro: formData.address.value?.logradouro,
            endereco_numero: formData.address.value?.numero,
            endereco_bairro: formData.address.value?.bairro,
            endereco_municipio: formData.address.value?.localidade,
            endereco_uf: formData.address.value?.uf,
            telefone: formData.phone.value,
            celular: formData.cellphone.value,
            email: formData.email.value,
            orgao_expedidor_identidade: formData.issuingAgency.value,
            numero_identidade: formData.rg.value,
            id_uf_identidade: formData.ufRg.value ? formData.ufRg.value : null,
        };

        const data = new FormData();

        data.append(
            "requestData",
            new Blob([JSON.stringify(personData)], { type: "application/json" })
        );

        if (fileInputRef.current && fileInputRef.current.getFile()) {
            const file = fileInputRef.current.getFile();
            data.append("foto", file.file, file.name);
        }

        if (formData.webcamImage.value) {
            data.append("webcam", formData.webcamImage.value);
        }

        return data;
    };

    const handleSavePerson = async (e) => {
        e.preventDefault();

        if (!formData || hasError({ formData, inputError })) return;

        try {
            const data = toFormData(formData);
            if (id) {
                await personsService.update(id, data);
            } else {
                await personsService.create(data);
            }
            showToast({ title: t(`settings.person.messages.success.${id ? "edit" : "create"}`), status: "success" });
            navigate(ROUTES.personPath);
        } catch (error) {
            showToast({ title: t(error), status: "error" });
        }
    };

    const isFormInvalid = ({ formData, inputError }) =>
        !formData ||
        hasError({ formData, inputError }) ||
        (formData.document?.value && !validateCpfOrCnpj(formData.document.value)) ||
        (formData.email?.value && !validateEmail(formData.email.value)) ||
        (formData.phone?.value && !validatePhone(formData.phone.value)) ||
        (formData.cellphone?.value && !validatePhone(formData.cellphone.value)) ||
        (formData.cep?.value && formData.cep.value.length > 0 && formData.cep.value.length < 9);

    useEffect(() => {
        if (id) {
            personsService.find(id)
                .then(initFormData)
                .catch((error) => showToast({ title: t(error), status: "error" }));
        } else {
            initFormData();
        }
    }, [id]);

    return (
        <Flex gap="5" direction="column" minH="89vh" align="center" pt={{ sm: "150px", md: "90px" }}>
            {formData ? (
                <Card>
                    <ListPageCardHeader
                        title={t(id ? 'settings.person.edit.title' : 'settings.person.new.title')}
                        subtitle={t(id ? 'settings.person.edit.subtitle' : 'settings.person.new.subtitle')}
                    />

                    <CardBody px="22px">
                        <Grid templateColumns={{ sm: "1fr", md: "repeat(2, 1fr)" }} gap="24px">
                            <FormControl
                                isRequired
                                isInvalid={!!formData.document.value && !validateCpfOrCnpj(formData.document.value)}
                            >
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("settings.person.form.cpf-cnpj.label")}
                                </FormLabel>
                                <Input
                                    variant="main"
                                    name="document"
                                    value={formData.document.value}
                                    onChange={handleChange}
                                    placeholder={t("settings.person.form.cpf-cnpj.placeholder")}
                                    fontSize="sm"
                                    required
                                />
                                {!formData.document.value && (
                                    <FormErrorMessage>
                                        {t("system.messages.errors.required-field")}
                                    </FormErrorMessage>
                                )}
                                {formData.document.value && !validateCpfOrCnpj(formData.document.value) && (
                                    <FormErrorMessage>
                                        {t("settings.person.form.invalid-document")}
                                    </FormErrorMessage>
                                )}
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("settings.person.form.name.label")}
                                </FormLabel>
                                <Input
                                    variant="main"
                                    name="name"
                                    value={formData.name.value}
                                    onChange={handleChange}
                                    placeholder={t("settings.person.form.name.placeholder")}
                                    fontSize="sm"
                                />
                                <FormErrorMessage>
                                    {t("system.messages.errors.required-field")}
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("settings.person.form.social-name.label")}
                                </FormLabel>
                                <Input
                                    variant="main"
                                    name="socialName"
                                    value={formData.socialName.value}
                                    onChange={handleChange}
                                    placeholder={t("settings.person.form.social-name.placeholder")}
                                    fontSize="sm"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("settings.person.form.surname.label")}
                                </FormLabel>
                                <Input
                                    variant="main"
                                    value={formData.surname.value}
                                    name="surname"
                                    onChange={handleChange}
                                    placeholder={t("settings.person.form.surname.placeholder")}
                                    fontSize="sm"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("settings.person.form.issuing-agency.label")}
                                </FormLabel>
                                <Input
                                    variant="main"
                                    value={formData.issuingAgency.value}
                                    name="issuingAgency"
                                    onChange={handleChange}
                                    placeholder={t("settings.person.form.issuing-agency.placeholder")}
                                    fontSize="sm"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("settings.person.form.rg.label")}
                                </FormLabel>
                                <Input
                                    variant="main"
                                    value={formData.rg.value}
                                    name="rg"
                                    onChange={handleChange}
                                    placeholder={t("settings.person.form.rg.placeholder")}
                                    fontSize="sm"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("settings.person.form.uf-rg.label")}
                                </FormLabel>
                                <Select
                                    variant="main"
                                    placeholder={t("settings.person.form.uf-rg.placeholder")}
                                    value={formData.ufRg.value}
                                    name="ufRg"
                                    onChange={handleChange}
                                    fontSize="sm"
                                >
                                    {ufs.map((uf) => (
                                        <option key={uf.value} value={uf.value}>
                                            {uf.value} - {uf.label}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl isInvalid={formData.cep.value.length > 0 && formData.cep.value.length < 9}>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("settings.person.form.cep.label")}
                                </FormLabel>
                                <Input
                                    variant="main"
                                    name="cep"
                                    value={formData.cep.value}
                                    onChange={handleCEPChange}
                                    placeholder={t("settings.person.form.cep.placeholder")}
                                    fontSize="sm"
                                />
                                {formData.cep.value.length > 0 && formData.cep.value.length < 9 && (
                                    <FormErrorMessage>{t("system.messages.errors.invalid-cep")}</FormErrorMessage>
                                )}
                            </FormControl>

                            <FormControl gridColumn="span 2">
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("settings.person.form.street.label")}
                                </FormLabel>
                                <Input
                                    variant="main"
                                    name="logradouro"
                                    value={formData.address.value.logradouro}
                                    onChange={handleAddressChange}
                                    placeholder={t("settings.person.form.street.placeholder")}
                                    fontSize="sm"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("settings.person.form.number.label")}
                                </FormLabel>
                                <Input
                                    variant="main"
                                    name="numero"
                                    value={formData.address.value.numero}
                                    onChange={handleAddressChange}
                                    placeholder={t("settings.person.form.number.placeholder")}
                                    fontSize="sm"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("settings.person.form.neighborhood.label")}
                                </FormLabel>
                                <Input
                                    variant="main"
                                    name="bairro"
                                    value={formData.address.value.bairro}
                                    onChange={handleAddressChange}
                                    placeholder={t("settings.person.form.neighborhood.placeholder")}
                                    fontSize="sm"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("settings.person.form.city.label")}
                                </FormLabel>
                                <Input
                                    variant="main"
                                    name="localidade"
                                    value={formData.address.value.localidade}
                                    onChange={handleAddressChange}
                                    placeholder={t("settings.person.form.city.placeholder")}
                                    fontSize="sm"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("settings.person.form.state.label")}
                                </FormLabel>
                                <Select
                                    variant="main"
                                    name="uf"
                                    value={formData.address.value.uf}
                                    onChange={handleAddressChange}
                                    placeholder={t("settings.person.form.state.placeholder")}
                                    fontSize="sm"
                                >
                                    {ufs.map((uf) => (
                                        <option key={uf.value} value={uf.value}>
                                            {uf.value} - {uf.label}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl
                                isInvalid={!!formData.phone.value && !validatePhone(formData.phone.value)}
                            >
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("settings.person.form.phone.label")}
                                </FormLabel>
                                <Input
                                    variant="main"
                                    value={formData.phone.value}
                                    name="phone"
                                    onChange={handleChange}
                                    maxLength={15}
                                    placeholder={t("settings.person.form.phone.placeholder")}
                                    fontSize="sm"
                                />
                                {formData.phone.value && !validatePhone(formData.phone.value) && (
                                    <FormErrorMessage>
                                        {t("settings.person.form.invalid-phone")}
                                    </FormErrorMessage>
                                )}
                            </FormControl>

                            <FormControl
                                isInvalid={!!formData.cellphone.value && !validatePhone(formData.cellphone.value)}
                            >
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("settings.person.form.cellphone.label")}
                                </FormLabel>
                                <Input
                                    variant="main"
                                    value={formData.cellphone.value}
                                    name="cellphone"
                                    onChange={handleChange}
                                    maxLength={15}
                                    placeholder={t("settings.person.form.cellphone.placeholder")}
                                    fontSize="sm"
                                />
                                {formData.cellphone.value && !validatePhone(formData.cellphone.value) && (
                                    <FormErrorMessage>
                                        {t("settings.person.form.invalid-cellphone")}
                                    </FormErrorMessage>
                                )}
                            </FormControl>

                            <FormControl
                                gridColumn="span 2"
                                isInvalid={!!formData.email.value && !validateEmail(formData.email.value)}
                            >
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("settings.person.form.email.label")}
                                </FormLabel>
                                <Input
                                    variant="main"
                                    value={formData.email.value}
                                    name="email"
                                    onChange={handleChange}
                                    placeholder={t("settings.person.form.email.placeholder")}
                                    fontSize="sm"
                                />
                                {formData.email.value && !validateEmail(formData.email.value) && (
                                    <FormErrorMessage>
                                        {t("settings.person.form.invalid-email")}
                                    </FormErrorMessage>
                                )}
                            </FormControl>

                            <FormControl>
                                <FileAttachmentInput
                                    ref={fileInputRef}
                                    id="foto"
                                    nameVariable={formData.photo.value}
                                    acceptedTypes={['image/jpeg']}
                                    label={t("settings.person.form.photo.label")}
                                    onChange={(file) =>
                                        setFormData({
                                            ...formData,
                                            photo: { ...formData.photo, value: file }
                                        })
                                    }
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="bold">
                                    {t("settings.person.form.webcam.label")}
                                </FormLabel>
                                <WebcamCapture
                                    onCapture={(img) =>
                                        setFormData({
                                            ...formData,
                                            webcamImage: { ...formData.webcamImage, value: img }
                                        })
                                    }
                                />
                            </FormControl>
                        </Grid>
                        <Flex w="100%" justify="space-between">
                            <Button
                                variant="no-effects"
                                alignSelf="flex-end"
                                w="125px"
                                bg="gray.100"
                                color="gray.700"
                                fontSize="sm"
                                fontWeight="bold"
                                onClick={() => navigate(ROUTES.personPath)}
                            >
                                {t("system.actions.cancel")}
                            </Button>
                            <Button
                                w="125px"
                                variant="dark"
                                fontSize="sm"
                                type="submit"
                                mt="24px"
                                _hover={{
                                    bg: "grey.500",
                                    color: "white"
                                }}
                                isDisabled={isFormInvalid({ formData, inputError })}
                                onClick={handleSavePerson}
                            >
                                {t(id ? "system.actions.edit" : "system.actions.save")}
                            </Button>
                        </Flex>
                    </CardBody>
                </Card>
            ) : (
                <Loading h="300px" w="100%" />
            )}
        </Flex>
    );
}

export default PersonEditor;